import type { AnalyticsConsent } from "./consent";
import {
  sanitizeEventProperties,
  type AnalyticsEventName,
  type AnalyticsEventProperties,
} from "./events";

export interface PostHogAdapter {
  init(token: string, options: Record<string, unknown>): unknown;
  capture(name: string, properties?: Record<string, unknown>): unknown;
  identify(distinctId: string): unknown;
  group(groupType: string, groupKey: string): unknown;
  reset(): unknown;
  opt_in_capturing(options?: { captureEventName?: string | null | false }): unknown;
  opt_out_capturing(): unknown;
}

export interface AnalyticsClient {
  setConsent(consent: AnalyticsConsent): Promise<void>;
  capture<Name extends AnalyticsEventName>(
    name: Name,
    properties: AnalyticsEventProperties[Name],
  ): Promise<void>;
  identifyCustomer(userId: string, organizationId: string): Promise<void>;
  clearIdentity(): Promise<void>;
  dispose(): void;
}

interface AnalyticsClientOptions {
  token: string | undefined;
  host: string | undefined;
  document: Document;
  view: Window;
  loadPostHog?: () => Promise<PostHogAdapter>;
}

const ALLOWED_HOSTS = new Set(["https://us.i.posthog.com"]);
const CUSTOM_EVENTS = new Set<AnalyticsEventName>([
  "marketing_page_view",
  "waitlist_cta_clicked",
  "product_signup_cta_clicked",
  "waitlist_submission_succeeded",
  "product_page_view",
  "customer_session_started",
  "saved_search_created",
  "property_analysis_requested",
  "property_analysis_completed",
  "checkout_started",
]);
const SYSTEM_IDENTITY_PROPERTIES = new Set([
  "distinct_id",
  "$distinct_id",
  "$anon_distinct_id",
  "$device_id",
  "$session_id",
  "$group_type",
  "$group_key",
  "$groups",
]);

function validConfiguration(token: string | undefined, host: string | undefined): boolean {
  return Boolean(
    token && /^phc_[A-Za-z0-9_-]{16,}$/.test(token) && host && ALLOWED_HOSTS.has(host),
  );
}

async function defaultLoader(): Promise<PostHogAdapter> {
  const module = await import("posthog-js");
  return module.default;
}

function clearPostHogPersistence(document: Document, view: Window): void {
  try {
    const keys: string[] = [];
    for (let index = 0; index < view.localStorage.length; index += 1) {
      const key = view.localStorage.key(index);
      if (key?.startsWith("ph_")) keys.push(key);
    }
    keys.forEach((key) => view.localStorage.removeItem(key));
  } catch {
    // Storage can be unavailable; analytics remains disabled regardless.
  }

  try {
    const cookieNames = document.cookie
      .split(";")
      .map((part) => part.trim().split("=", 1)[0])
      .filter((name): name is string => Boolean(name?.startsWith("ph_")));
    for (const name of cookieNames) {
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
      document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.veracio.app; SameSite=Lax; Secure`;
    }
  } catch {
    // Cookie access can be unavailable; analytics remains disabled regardless.
  }
}

function validInternalId(value: string): boolean {
  return value.length > 0 && value.length <= 128 && /^[A-Za-z0-9_-]+$/.test(value);
}

function safeIdentityProperties(properties: Record<string, unknown>): Record<string, unknown> {
  const safe: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (!SYSTEM_IDENTITY_PROPERTIES.has(key)) continue;
    if (key === "$groups") {
      const organizationId =
        typeof value === "object" && value !== null
          ? (value as Record<string, unknown>)["organization"]
          : undefined;
      if (typeof organizationId === "string" && validInternalId(organizationId))
        safe[key] = { organization: organizationId };
      continue;
    }
    if (key === "$group_type") {
      if (value === "organization") safe[key] = value;
      continue;
    }
    if (typeof value === "string" && validInternalId(value)) safe[key] = value;
  }
  return safe;
}

function scrubBeforeSend(event: unknown, projectToken: string): unknown {
  if (!event || typeof event !== "object") return null;
  const candidate = event as { event?: unknown; properties?: unknown };
  if (typeof candidate.event !== "string") return null;
  const properties =
    candidate.properties && typeof candidate.properties === "object"
      ? (candidate.properties as Record<string, unknown>)
      : {};
  const requiredToken = properties["token"] === projectToken ? { token: projectToken } : {};
  if (candidate.event === "$identify" || candidate.event === "$groupidentify") {
    return {
      ...candidate,
      properties: { ...safeIdentityProperties(properties), ...requiredToken },
    };
  }
  if (!CUSTOM_EVENTS.has(candidate.event as AnalyticsEventName)) return null;
  const name = candidate.event as AnalyticsEventName;
  return {
    ...candidate,
    properties: {
      ...sanitizeEventProperties(name, properties as never),
      ...safeIdentityProperties(properties),
      ...requiredToken,
    },
  };
}

export function createAnalyticsClient(options: AnalyticsClientOptions): AnalyticsClient {
  const token = options.token?.trim();
  const host = options.host?.trim().replace(/\/$/, "");
  const configured = validConfiguration(token, host);
  const loadPostHog = options.loadPostHog ?? defaultLoader;
  let consent: AnalyticsConsent = "unknown";
  let instance: PostHogAdapter | undefined;
  let loading: Promise<PostHogAdapter | undefined> | undefined;
  let disposed = false;
  let loadFailed = false;
  let generation = 0;

  const ensureReady = async (): Promise<PostHogAdapter | undefined> => {
    if (disposed || consent !== "granted" || !configured || loadFailed) return undefined;
    if (instance) return instance;
    if (!loading) {
      const requestedGeneration = generation;
      loading = loadPostHog()
        .then((posthog) => {
          if (disposed || consent !== "granted" || generation !== requestedGeneration)
            return undefined;
          posthog.init(token!, {
            api_host: host!,
            ui_host: "https://us.posthog.com",
            autocapture: false,
            capture_pageview: false,
            capture_pageleave: false,
            disable_session_recording: true,
            disable_surveys: true,
            advanced_disable_flags: true,
            advanced_disable_feature_flags: true,
            advanced_disable_feature_flags_on_first_load: true,
            person_profiles: "identified_only",
            persistence: "localStorage+cookie",
            opt_out_persistence_by_default: true,
            request_batching: false,
            respect_dnt: true,
            before_send: (event: unknown) => scrubBeforeSend(event, token!),
          });
          instance = posthog;
          posthog.opt_in_capturing({ captureEventName: false });
          return posthog;
        })
        .catch(() => {
          loadFailed = true;
          return undefined;
        });
    }
    return loading;
  };

  return {
    async setConsent(next) {
      if (disposed || next === consent) return;
      consent = next;
      generation += 1;
      if (next === "granted") {
        if (instance) instance.opt_in_capturing({ captureEventName: false });
        else {
          loading = undefined;
          await ensureReady();
        }
        return;
      }
      instance?.opt_out_capturing();
      instance?.reset();
      clearPostHogPersistence(options.document, options.view);
    },
    async capture(name, properties) {
      const posthog = await ensureReady();
      if (!posthog || disposed || consent !== "granted") return;
      posthog.capture(name, sanitizeEventProperties(name, properties));
    },
    async identifyCustomer(userId, organizationId) {
      if (!validInternalId(userId) || !validInternalId(organizationId)) return;
      const posthog = await ensureReady();
      if (!posthog || disposed || consent !== "granted") return;
      posthog.identify(userId);
      posthog.group("organization", organizationId);
    },
    async clearIdentity() {
      const posthog = await ensureReady();
      if (!posthog || disposed || consent !== "granted") return;
      posthog.reset();
    },
    dispose() {
      disposed = true;
      generation += 1;
    },
  };
}
